import { createContext, useContext, useState, useMemo, useCallback } from "react";

const dict = {
  en: {
    nav: { about: "About", how: "How it works", plans: "Plans", stories: "Stories", blog: "Blog", contact: "Contact", book: "Book Free Consultation", admin: "Admin" },
    hero: {
      eyebrow: "Hire your DietWalaa",
      title_line_1: "You focus on work.",
      title_line_2: "We handle the food.",
      tagline: "Fuel your body. Fuel your life.",
      sub: "Real food, real routines. Diet plans built around your kitchen, your shifts, and your travel — by a qualified dietitian and a team who actually follow up.",
      cta_primary: "Book FREE Consultation",
      cta_secondary: "See Plans",
      trust_years: "10+ Years",
      trust_years_sub: "Trusted diet care",
      trust_rating: "4.6 / 5",
      trust_rating_sub: "Average client rating",
    },
    audience: {
      title: "Built for Indians — at home and abroad",
      india: "Chandigarh · Panchkula · Mohali · Delhi NCR",
      india_sub: "Walk-in & online consultations across India",
      canada: "Canada · US · UK · Australia · Middle East",
      canada_sub: "NRI plans with local grocery mapping",
      usa: "Anywhere you live · Online",
      usa_sub: "1-to-1 online consultations worldwide",
    },
    usp: {
      title: "What makes us different",
      items: [
        { t: "Road-friendly meals", d: "Plans that survive highways, dhabas, and gas-station stops." },
        { t: "Home food, not fantasy food", d: "We work with what your kitchen already cooks. No exotic imports." },
        { t: "No starving, no extremes", d: "Sustainable calories. You eat, you lose fat, you keep it off." },
        { t: "Night-shift & travel ready", d: "Timing that flexes with your shifts, jet-lag and long hauls." },
        { t: "One-to-one team support", d: "Direct follow-ups from our team. You are never on your own." },
        { t: "Weekly progress tuning", d: "We track. We adjust. No copy-paste PDFs." },
      ],
    },
    how: {
      title: "How it works",
      steps: [
        "We understand your routine",
        "We assign you a dietitian",
        "You get a plan you can actually follow",
        "We track progress every week",
        "We adjust till your goal is real",
      ],
    },
    about: {
      eyebrow: "Meet the founder",
      title: "Dt. Manthan Sharma — Founder, DietWalaa",
      body_p1: "Dt. Manthan Sharma is a nutrition professional with a rare blend of bodybuilding, food science, clinical nutrition, and quality management. His early years in bodybuilding gave him a hands-on understanding of body transformation, metabolism, and the discipline needed for sustainable results.",
      body_p2: "With an academic background in Food Technology and experience across clinical nutrition and the food industry, Manthan brings a scientific yet practical approach — successful health transformation isn't a temporary diet, it's a personalized plan built around your lifestyle, medical needs, cultural preferences and long-term goals.",
      body_p3: "Through DietWalaa, he has helped clients achieve real improvements in weight management, diabetes reversal, lifestyle disorders and overall wellness — supported by tracked progress, client reports and continuous monitoring.",
      body_p4: "DietWalaa focuses on evidence-based, personalized nutrition programs for Indians at home and Indians abroad — so you get healthier without giving up the traditional food you actually love.",
      pill_1: "Bodybuilding background",
      pill_2: "Food Technology",
      pill_3: "Clinical Nutrition",
      pill_4: "Quality Management",
    },
    plans: {
      title: "Diet plans that fit your life",
      sub: "One-to-one plans, tuned weekly. Choose the length that fits your goal.",
      tabs: { m1: "1 Month", m3: "3 Months", m6: "6 Months" },
      cta: "Get this plan",
      list: [
        {
          key: "m1",
          name: "Kickstart",
          price: "₹2,499",
          price_nri: "CAD $79 / USD $59",
          tag: "Best for a reset",
          features: ["1 personalised plan", "Weekly follow-up call", "WhatsApp support", "Grocery list included", "One plan revision"],
        },
        {
          key: "m3",
          name: "Transformation",
          price: "₹5,999",
          price_nri: "CAD $199 / USD $149",
          tag: "Most popular",
          features: ["3 personalised plans (monthly)", "Bi-weekly review calls", "Priority WhatsApp support", "NRI grocery mapping", "Free plan revisions"],
          highlight: true,
        },
        {
          key: "m6",
          name: "Lifestyle",
          price: "₹9,999",
          price_nri: "CAD $349 / USD $269",
          tag: "For lasting change",
          features: ["6 personalised plans", "Weekly review + coaching", "Priority WhatsApp support", "Travel & shift protocols", "Body composition targets"],
        },
      ],
    },
    testimonials: {
      title: "Happy stories",
      list: [
        { name: "Simran Kaur", gender: "f", city: "Brampton, ON · NRI", stars: 5, text: "I was gaining weight since moving to Canada. DietWalaa's plan actually used Indian grocery from local stores. Down 8 kg in 4 months — and I never gave up rotis or my mom's dal." },
        { name: "Rahul Mehta", gender: "m", city: "Mumbai · Online client", stars: 5, text: "Corporate desk job, endless client dinners, no time to gym. Manthan built a plan around my real schedule — not a fantasy one. Down 12 kg in 5 months without skipping a single business dinner." },
        { name: "Priya Sharma", gender: "f", city: "Delhi NCR · Walk-in", stars: 5, text: "Walked in for a consultation, expected the usual 'eat less, move more' lecture. Instead I got a properly personalised plan and real weekly follow-ups. Lost 9 kg and got my energy back." },
        { name: "Harpreet Singh", gender: "m", city: "Surrey, BC · Long-haul trucker", stars: 5, text: "I do the BC-to-Texas route, 5-6 days out at a time. The team built meals I can actually eat at truck stops along I-5. Lost 11 kg in 6 months — my last physical was the best in a decade." },
        { name: "Ananya Iyer", gender: "f", city: "Bangalore · Online", stars: 5, text: "PCOS made weight loss feel impossible for years. The DietWalaa team built a plan around my cycle, not against it. Six months in, my reports and my periods have completely changed." },
        { name: "Dr. Gurpreet Sidhu", gender: "m", city: "Chandigarh · Sector 8", stars: 5, text: "Walked in expecting a sales pitch. Instead got an honest, science-based plan. As a physician myself I appreciated the evidence-based approach. HbA1c down from 7.4 to 5.9 in 7 months." },
        { name: "Jasleen Brar", gender: "f", city: "Mississauga, ON · RN Night Shift", stars: 5, text: "Working 12-hour night shifts at the hospital was destroying my sleep and my weight. Their shift protocol changed both. I feel energetic even at 4 AM without living on Tim Hortons anymore." },
        { name: "Aditya Reddy", gender: "m", city: "Hyderabad → Dallas, TX", stars: 5, text: "Moved to the US for work and lost all discipline in 2 years. Manthan's plan used Whole Foods / H-Mart / Patel Brothers ingredients. Lost 14 kg and got my BP under control." },
      ],
    },
    blog: { title: "From the journal", sub: "Practical, no-nonsense diet reads for real Indian lives — at home and abroad.", read: "Read article" },
    contact: {
      title: "Talk to DietWalaa",
      sub: "Free 15-minute counselling. Tell us your goal, we'll tell you honestly if we can help.",
      name: "Full name",
      phone: "Phone number",
      email: "Email (optional)",
      city: "City",
      goal: "Your goal (e.g. lose 8 kg, control sugar)",
      submit: "Request callback",
      thanks: "Thank you! We'll call you back within 24 hours.",
      phone_direct: "Call / WhatsApp",
    },
    lead_modal: {
      badge: "Free consultation",
      title: "Let's talk about your diet plan",
      sub: "NRI or India-based, walk-in or online — drop your number, our team will call you within 24 hours. No spam, no auto-messages.",
      phone: "Phone (with country code)",
      name: "Name (optional)",
      audience: "Where are you based?",
      india: "India",
      canada: "Canada (Brampton / Vancouver)",
      usa: "USA",
      submit: "Get my call back",
      later: "Maybe later",
    },
    footer: { rights: "All rights reserved.", tagline: "Real food. Real routines. Real results." },
  },
  hi: {
    nav: { about: "हमारे बारे में", how: "कैसे काम करता है", plans: "प्लान्स", stories: "कहानियाँ", blog: "ब्लॉग", contact: "संपर्क", book: "फ्री कंसल्टेशन बुक करें", admin: "एडमिन" },
    hero: {
      eyebrow: "अपना DietWalaa चुनिए",
      title_line_1: "आप काम पर ध्यान दो।",
      title_line_2: "खाना हम पर छोड़ो।",
      tagline: "शरीर को ईंधन दो. ज़िंदगी को ईंधन दो.",
      sub: "असली खाना, असली रूटीन। आपकी रसोई, शिफ्ट और सफ़र के हिसाब से बना डाइट प्लान — क्वालिफाइड डाइटिशियन और एक टीम, जो सच में फॉलो-अप करती है।",
      cta_primary: "फ्री कंसल्टेशन बुक करें",
      cta_secondary: "प्लान देखें",
      trust_years: "10+ साल",
      trust_years_sub: "भरोसेमंद डाइट केयर",
      trust_rating: "4.6 / 5",
      trust_rating_sub: "औसत क्लाइंट रेटिंग",
    },
    audience: {
      title: "भारतीयों के लिए — देश में और विदेश में",
      india: "चंडीगढ़ · पंचकुला · मोहाली · दिल्ली NCR",
      india_sub: "पूरे भारत में वॉक-इन और ऑनलाइन कंसल्टेशन",
      canada: "कनाडा · US · UK · ऑस्ट्रेलिया · मिडिल ईस्ट",
      canada_sub: "लोकल ग्रोसरी मैपिंग के साथ NRI प्लान",
      usa: "कहीं भी रहें · ऑनलाइन",
      usa_sub: "दुनिया भर में 1-to-1 ऑनलाइन कंसल्टेशन",
    },
    usp: {
      title: "हम अलग क्यों हैं",
      items: [
        { t: "सफ़र-अनुकूल मील्स", d: "जो हाईवे, ढाबे और पेट्रोल पंप पर भी चलें।" },
        { t: "घर का खाना, फैंसी नहीं", d: "आपकी रसोई का सच्चा खाना — कोई विदेशी सामान नहीं।" },
        { t: "भूखे रहना नहीं", d: "टिकाऊ कैलोरी। वज़न घटेगा, वापिस नहीं आएगा।" },
        { t: "नाइट शिफ्ट + ट्रैवल", d: "आपकी टाइमिंग, जेट-लैग और लॉन्ग-हॉल के अनुसार।" },
        { t: "वन-टू-वन सपोर्ट", d: "टीम रोज़ फॉलो-अप करती है। आप अकेले नहीं।" },
        { t: "हर हफ़्ते ट्यूनिंग", d: "हम ट्रैक करते हैं, बदलते हैं। कोई कॉपी-पेस्ट PDF नहीं।" },
      ],
    },
    how: {
      title: "कैसे काम करता है",
      steps: [
        "हम आपका रूटीन समझते हैं",
        "आपको डाइटिशियन असाइन होता है",
        "आपको ऐसा प्लान मिलता है जो आप सच में फॉलो कर सकें",
        "हर हफ़्ते प्रोग्रेस ट्रैक",
        "जब तक लक्ष्य पूरा नहीं होता, बदलाव",
      ],
    },
    about: {
      eyebrow: "फ़ाउंडर से मिलिए",
      title: "डॉ. मंथन शर्मा — फ़ाउंडर, DietWalaa",
      body_p1: "डॉ. मंथन शर्मा एक ऐसे न्यूट्रिशन प्रोफेशनल हैं जिनके पास बॉडीबिल्डिंग, फ़ूड साइंस, क्लिनिकल न्यूट्रिशन और क्वालिटी मैनेजमेंट का दुर्लभ संगम है। शुरुआती बॉडीबिल्डिंग के दिनों ने उन्हें बॉडी ट्रांसफ़ॉर्मेशन, मेटाबॉलिज़्म और अनुशासन का व्यावहारिक ज्ञान दिया।",
      body_p2: "फ़ूड टेक्नोलॉजी की एकेडमिक पृष्ठभूमि और क्लिनिकल न्यूट्रिशन एवं फ़ूड इंडस्ट्री का अनुभव — मंथन एक साइंटिफ़िक और प्रैक्टिकल दृष्टिकोण लाते हैं। सच्चा हेल्थ ट्रांसफ़ॉर्मेशन कोई अस्थायी डाइट नहीं, बल्कि आपकी लाइफ़स्टाइल, मेडिकल ज़रूरतों, कल्चरल पसंद और लंबे लक्ष्यों के हिसाब से बना पर्सनल प्लान है।",
      body_p3: "DietWalaa के ज़रिए उन्होंने वेट मैनेजमेंट, डायबिटीज़ रिवर्सल, लाइफ़स्टाइल डिसऑर्डर्स और ओवरऑल वेलनेस में क्लाइंट्स को सच्चे नतीजे दिलाए हैं — प्रोग्रेस ट्रैकिंग, रिपोर्ट्स और लगातार निगरानी के साथ।",
      body_p4: "DietWalaa भारत में रहने वालों और विदेश में बसे भारतीयों — दोनों के लिए एविडेंस-आधारित पर्सनल न्यूट्रिशन प्रोग्राम बनाता है — ताकि आप अपने पारंपरिक खाने को छोड़े बिना स्वस्थ रह सकें।",
      pill_1: "बॉडीबिल्डिंग बैकग्राउंड",
      pill_2: "फ़ूड टेक्नोलॉजी",
      pill_3: "क्लिनिकल न्यूट्रिशन",
      pill_4: "क्वालिटी मैनेजमेंट",
    },
    plans: {
      title: "आपकी ज़िंदगी में फिट होने वाले प्लान्स",
      sub: "वन-टू-वन प्लान, हर हफ़्ते ट्यून। अपनी अवधि चुनें।",
      tabs: { m1: "1 महीना", m3: "3 महीने", m6: "6 महीने" },
      cta: "यह प्लान लें",
      list: [
        { key: "m1", name: "किकस्टार्ट", price: "₹2,499", price_nri: "CAD $79 / USD $59", tag: "रीसेट के लिए बेहतरीन", features: ["1 पर्सनल प्लान", "साप्ताहिक कॉल", "WhatsApp सपोर्ट", "ग्रोसरी लिस्ट", "एक बार प्लान बदलाव"] },
        { key: "m3", name: "ट्रांसफॉर्मेशन", price: "₹5,999", price_nri: "CAD $199 / USD $149", tag: "सबसे लोकप्रिय", features: ["3 पर्सनल प्लान (महीनेवार)", "दो-हफ़्ते रिव्यू कॉल", "प्रायोरिटी WhatsApp", "NRI ग्रोसरी मैपिंग", "फ्री बदलाव"], highlight: true },
        { key: "m6", name: "लाइफस्टाइल", price: "₹9,999", price_nri: "CAD $349 / USD $269", tag: "लम्बे बदलाव के लिए", features: ["6 पर्सनल प्लान", "साप्ताहिक कोचिंग", "प्रायोरिटी सपोर्ट", "ट्रैवल + शिफ्ट प्रोटोकॉल", "बॉडी कम्पोज़िशन लक्ष्य"] },
      ],
    },
    testimonials: {
      title: "खुशी की कहानियाँ",
      list: [
        { name: "सिमरन कौर", gender: "f", city: "ब्रैम्पटन, ON · NRI", stars: 5, text: "कनाडा आने के बाद वज़न बढ़ता जा रहा था। DietWalaa का प्लान लोकल इंडियन ग्रोसरी से ही चला। 4 महीने में 8 किलो कम — रोटी छोड़े बिना।" },
        { name: "राहुल मेहता", gender: "m", city: "मुंबई · ऑनलाइन क्लाइंट", stars: 5, text: "कॉर्पोरेट डेस्क जॉब, क्लाइंट डिनर्स, जिम का समय नहीं। मंथन ने प्लान मेरे असली शेड्यूल के हिसाब से बनाया। 5 महीने में 12 किलो कम — बिना डिनर मिस किए।" },
        { name: "प्रिया शर्मा", gender: "f", city: "दिल्ली NCR · वॉक-इन", stars: 5, text: "वॉक-इन कंसल्टेशन के लिए गई। कोई भाषण नहीं, बल्कि सच्चा पर्सनलाइज़्ड प्लान और हर हफ़्ते फ़ॉलो-अप। 9 किलो कम और एनर्जी वापस।" },
        { name: "हरप्रीत सिंह", gender: "m", city: "सरी, BC · लॉन्ग-हॉल ट्रक ड्राइवर", stars: 5, text: "मैं BC से टेक्सस तक का रूट चलाता हूँ — 5-6 दिन बाहर। टीम ने वो मील्स बनाए जो I-5 के ट्रक स्टॉप्स पर सच में मिलते हैं। 6 महीने में 11 किलो कम।" },
        { name: "अनन्या अय्यर", gender: "f", city: "बेंगलुरु · ऑनलाइन", stars: 5, text: "PCOS की वजह से वज़न कम करना नामुमकिन लगता था। DietWalaa ने मेरे साइकल के हिसाब से प्लान बनाया। 6 महीने में रिपोर्ट्स और पीरियड्स — दोनों बदल गए।" },
        { name: "डॉ. गुरप्रीत सिद्धू", gender: "m", city: "चंडीगढ़ · सेक्टर 8", stars: 5, text: "वॉक-इन गया, बिकवाली की उम्मीद थी। पर मिला ईमानदार, साइंस-बेस्ड प्लान। डॉक्टर होने के नाते evidence-based approach अच्छा लगा। HbA1c 7.4 → 5.9।" },
        { name: "जसलीन बराड़", gender: "f", city: "मिसिसॉगा, ON · नर्स नाइट शिफ्ट", stars: 5, text: "हॉस्पिटल में 12-घंटे की नाइट शिफ्ट ने नींद और वज़न दोनों बिगाड़ दिए थे। शिफ्ट-प्रोटोकॉल से दोनों ठीक हैं।" },
        { name: "आदित्य रेड्डी", gender: "m", city: "हैदराबाद → डलास, TX", stars: 5, text: "अमेरिका आने के बाद 2 साल में सब खो दिया था। मंथन ने Whole Foods / Patel Brothers की सामग्री से प्लान बनाया। 14 किलो कम और BP कंट्रोल।" },
      ],
    },
    blog: { title: "जर्नल से", sub: "असली पंजाबी ज़िंदगी के लिए प्रैक्टिकल डाइट लेख।", read: "पढ़ें" },
    contact: {
      title: "DietWalaa से बात करें",
      sub: "15 मिनट का फ्री काउंसलिंग। लक्ष्य बताइए, हम बताएंगे कि हम मदद कर सकते हैं या नहीं।",
      name: "पूरा नाम",
      phone: "फ़ोन नंबर",
      email: "ईमेल (वैकल्पिक)",
      city: "शहर",
      goal: "आपका लक्ष्य (जैसे 8 किलो कम, शुगर कंट्रोल)",
      submit: "कॉलबैक चाहिए",
      thanks: "धन्यवाद! हम 24 घंटे में कॉल करेंगे।",
      phone_direct: "कॉल / WhatsApp",
    },
    lead_modal: {
      badge: "फ्री कंसल्टेशन",
      title: "आपके डाइट प्लान की बात करें",
      sub: "NRI हों या भारत में — नंबर छोड़िए, हम 24 घंटे में कॉल करेंगे। कोई स्पैम नहीं।",
      phone: "फ़ोन (देश कोड सहित)",
      name: "नाम (वैकल्पिक)",
      audience: "आप कहाँ हैं?",
      india: "भारत",
      canada: "कनाडा",
      usa: "USA",
      submit: "कॉलबैक भेजें",
      later: "बाद में",
    },
    footer: { rights: "सर्वाधिकार सुरक्षित।", tagline: "असली खाना. असली रूटीन. असली नतीजे." },
  },
};

const LangContext = createContext({ lang: "en", t: dict.en, setLang: () => {} });

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("dw_lang") || "en");
  const change = useCallback((l) => {
    setLang(l);
    localStorage.setItem("dw_lang", l);
  }, []);
  const value = useMemo(() => ({ lang, t: dict[lang] || dict.en, setLang: change }), [lang, change]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
